import { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Badge, Loader, Checkbox, Alert } from '@mantine/core';
import {
    IconWand,
    IconAlertCircle,
    IconCheck,
    IconArrowRight,
    IconFileText,
    IconReceipt,
    IconRefresh
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { formatDay, formatPrice } from '@/lib/format-utils';
import { Link } from 'react-router-dom';
import { PAYMENTS_PATH, RESERVATIONS_PATH } from '@/lib/navigation';
import { useAutoAssignPaymentsPreview, useAutoAssignPaymentsConfirm } from '@/hooks/api-hooks';
import { notify } from '@/lib/notify';
import type { AutoAssignPreviewResult, PaymentPreviewPlan } from '@turizi/shared';

interface Props {
    opened: boolean;
    onClose: () => void;
    businessId?: number;
    paymentId?: number;
    onSuccess?: () => void;
}

export default function AutoAssignPaymentsModal({
    opened,
    onClose,
    businessId,
    paymentId,
    onSuccess,
}: Props) {
    const { t } = useTranslation();

    const previewMutation = useAutoAssignPaymentsPreview();
    const confirmMutation = useAutoAssignPaymentsConfirm();

    const [previewResult, setPreviewResult] = useState<AutoAssignPreviewResult | null>(null);
    const [selectedPaymentIds, setSelectedPaymentIds] = useState<Set<number>>(new Set());

    const loadPreview = () => {
        setPreviewResult(null);
        setSelectedPaymentIds(new Set());
        previewMutation.mutate(
            { business_id: businessId, payment_id: paymentId },
            {
                onSuccess: (res: any) => {
                    const data: AutoAssignPreviewResult = res.data;
                    setPreviewResult(data);

                    const assignableIds = new Set<number>();
                    (data.payments || []).forEach(p => {
                        if (p.status === 'assigned' || p.status === 'partially_assigned') {
                            assignableIds.add(p.payment_id);
                        }
                    });
                    setSelectedPaymentIds(assignableIds);
                },
                onError: () => {
                    notify.error(t('operation.error'), t('payments_auto_assigned_error'));
                }
            }
        );
    };

    useEffect(() => {
        if (opened) {
            loadPreview();
        } else {
            setPreviewResult(null);
            setSelectedPaymentIds(new Set());
        }
    }, [opened, businessId, paymentId]);

    const assignablePayments = useMemo(() => {
        if (!previewResult?.payments) return [];
        return previewResult.payments.filter(
            p => p.status === 'assigned' || p.status === 'partially_assigned'
        );
    }, [previewResult]);

    const togglePayment = (id: number) => {
        setSelectedPaymentIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleSelectAll = () => {
        const next = new Set<number>();
        assignablePayments.forEach(p => next.add(p.payment_id));
        setSelectedPaymentIds(next);
    };

    const handleDeselectAll = () => {
        setSelectedPaymentIds(new Set());
    };

    const handleConfirm = () => {
        if (!previewResult) return;

        const filteredAssignments = previewResult.payments
            .filter(p => selectedPaymentIds.has(p.payment_id) && p.assignments.length > 0)
            .map(p => ({
                payment_id: p.payment_id,
                assignments: p.assignments.map(a => ({
                    type: a.type,
                    target_id: a.target_id,
                    amount: a.amount
                }))
            }));

        confirmMutation.mutate(
            {
                business_id: businessId,
                payment_id: paymentId,
                assignments: filteredAssignments
            },
            {
                onSuccess: () => {
                    notify.success(t('operation.success'), t('payments_auto_assigned_success'));
                    onSuccess?.();
                    onClose();
                },
                onError: () => {
                    notify.error(t('operation.error'), t('payments_auto_assigned_error'));
                }
            }
        );
    };

    const selectedCount = selectedPaymentIds.size;

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            size="xl"
            title={
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-amber-100 text-amber-800 rounded-lg">
                        <IconWand size={18} />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide">
                            {t('auto_assign_payments_title')}
                        </h3>
                        <p className="text-xs text-slate-500 font-normal">
                            {t('auto_assign_payments_desc')}
                        </p>
                    </div>
                </div>
            }
            centered
        >
            <div className="space-y-4 py-1">
                {previewMutation.isPending ? (
                    <div className="flex flex-col items-center justify-center py-12 space-y-3">
                        <Loader size="md" color="amber" />
                        <span className="text-xs font-semibold text-slate-600">
                            {t('calculating_auto_assignments')}
                        </span>
                    </div>
                ) : previewMutation.isError ? (
                    <Alert
                        color="red"
                        icon={<IconAlertCircle size={16} />}
                        title={t('error')}
                    >
                        <div className="flex items-center justify-between">
                            <span>{t('payments_auto_assigned_error')}</span>
                            <Button size="xs" variant="light" color="red" leftSection={<IconRefresh size={14} />} onClick={loadPreview}>
                                {t('retry')}
                            </Button>
                        </div>
                    </Alert>
                ) : !previewResult || previewResult.payments.length === 0 ? (
                    <div className="py-8 text-center text-slate-500 text-sm">
                        {t('auto_assign_no_payments_to_assign')}
                    </div>
                ) : (
                    <>
                        {/* Summary Bar */}
                        <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3 text-xs">
                            <div className="flex items-center gap-3">
                                <div>
                                    <span className="text-slate-500 font-medium">{t('analyzed_payments')}: </span>
                                    <span className="font-bold text-slate-800">{previewResult.total_payments_checked}</span>
                                </div>
                                <div className="h-4 w-px bg-slate-300" />
                                <div>
                                    <span className="text-emerald-700 font-medium">{t('can_assign')}: </span>
                                    <span className="font-bold text-emerald-800">{previewResult.assignable_payments_count}</span>
                                </div>
                                <div className="h-4 w-px bg-slate-300" />
                                <div>
                                    <span className="text-slate-500 font-medium">{t('unassignable')}: </span>
                                    <span className="font-bold text-slate-700">{previewResult.unassignable_payments_count}</span>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <span className="text-slate-500 font-medium">{t('total_to_assign')}:</span>
                                {Object.entries(previewResult.total_amount_assigned_by_currency).map(([curr, amt]) => (
                                    <Badge key={curr} color="amber" variant="filled" size="sm" className="font-bold">
                                        ${formatPrice(amt)} {curr}
                                    </Badge>
                                ))}
                            </div>
                        </div>

                        {/* Selection controls */}
                        {assignablePayments.length > 1 && (
                            <div className="flex items-center justify-between text-xs text-slate-500 px-1">
                                <span>{t('assignments_preview')}</span>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        className="text-blue-650 hover:underline font-semibold cursor-pointer"
                                        onClick={handleSelectAll}
                                    >
                                        {t('select_all')}
                                    </button>
                                    <span>&bull;</span>
                                    <button
                                        type="button"
                                        className="text-slate-500 hover:underline font-semibold cursor-pointer"
                                        onClick={handleDeselectAll}
                                    >
                                        {t('deselect_all')}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* List of Payments */}
                        <div className="space-y-3 max-h-[58vh] overflow-y-auto pr-1">
                            {previewResult.payments.map((plan: PaymentPreviewPlan) => {
                                const isAssignable = plan.status === 'assigned' || plan.status === 'partially_assigned';
                                const isChecked = selectedPaymentIds.has(plan.payment_id);

                                return (
                                    <div
                                        key={plan.payment_id}
                                        className={`border rounded-xl p-3.5 transition-colors ${
                                            !isAssignable
                                                ? 'bg-slate-50/70 border-slate-200/70 opacity-75'
                                                : isChecked
                                                ? 'bg-white border-amber-300 shadow-2xs'
                                                : 'bg-white border-slate-200/90'
                                        }`}
                                    >
                                        {/* Card Header */}
                                        <div className="flex items-start justify-between gap-3 pb-2.5 border-b border-slate-100">
                                            <div className="flex items-center gap-2.5 min-w-0">
                                                <Checkbox
                                                    size="xs"
                                                    color="amber"
                                                    disabled={!isAssignable}
                                                    checked={isChecked}
                                                    onChange={() => togglePayment(plan.payment_id)}
                                                />
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <Link
                                                        to={`${PAYMENTS_PATH}/${plan.payment_id}`}
                                                        target="_blank"
                                                        className="font-bold text-xs text-blue-650 hover:underline"
                                                    >
                                                        #{plan.payment_id}
                                                    </Link>
                                                    <Badge size="xs" color="gray" variant="light">
                                                        {formatDay(plan.payment.payment_date || plan.payment.due_date, '') || '-'}
                                                    </Badge>
                                                    {plan.direction === 'IN' ? (
                                                        <Badge size="xs" color="emerald" variant="light" className="font-semibold">
                                                            {t('payment_from_client')}
                                                        </Badge>
                                                    ) : plan.direction === 'OUT' ? (
                                                        <Badge size="xs" color="indigo" variant="light" className="font-semibold">
                                                            {t('payment_to_provider')}
                                                        </Badge>
                                                    ) : (
                                                        <Badge size="xs" color="gray" variant="light">
                                                            {plan.direction}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="text-right shrink-0">
                                                <div className="text-xs font-bold text-slate-800">
                                                    ${formatPrice(plan.total_amount)}{' '}
                                                    <span className="text-xxs text-slate-500 font-normal">{plan.payment.currency}</span>
                                                </div>
                                                <div className="text-[11px] text-slate-500 truncate max-w-[180px]">
                                                    {plan.payment.from_name || t('none')} &rarr; {plan.payment.to_name || t('none')}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Card Status & Details */}
                                        <div className="pt-2">
                                            {plan.status === 'assigned' ? (
                                                <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 mb-2">
                                                    <IconCheck size={14} className="text-emerald-600" />
                                                    <span>{t('will_assign')} (${formatPrice(plan.assigned_amount)} {plan.payment.currency})</span>
                                                </div>
                                            ) : plan.status === 'partially_assigned' ? (
                                                <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 mb-2">
                                                    <IconAlertCircle size={14} className="text-amber-600" />
                                                    <span>
                                                        {t('partial_assignment')} (${formatPrice(plan.assigned_amount)} {plan.payment.currency}) &bull;{' '}
                                                        ${formatPrice(plan.unallocated_amount)} {plan.payment.currency} {t('remains_unallocated')}
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                                    <IconAlertCircle size={14} className="text-slate-400 shrink-0" />
                                                    <span>
                                                        {t('cannot_assign')}: {plan.reason ? t(plan.reason) : ''}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Assignments Table */}
                                            {plan.assignments.length > 0 && (
                                                <div className="mt-2 space-y-1.5 bg-slate-50/60 rounded-lg p-2.5 border border-slate-100 text-xs">
                                                    <div className="grid grid-cols-12 text-[10px] font-bold text-slate-400 uppercase tracking-wider pb-1 border-b border-slate-200/50">
                                                        <span className="col-span-6">{t('target_item')}</span>
                                                        <span className="col-span-3 text-right">{t('allocated_amount')}</span>
                                                        <span className="col-span-3 text-right">{t('balance_before_after')}</span>
                                                    </div>

                                                    {plan.assignments.map((assign, idx) => (
                                                        <div key={idx} className="grid grid-cols-12 items-center py-1 border-b border-slate-100 last:border-b-0">
                                                            <div className="col-span-6 flex items-center gap-1.5 min-w-0 pr-2">
                                                                {assign.type === 'reservation' ? (
                                                                    <>
                                                                        <IconFileText size={13} className="text-blue-600 shrink-0" />
                                                                        <Link
                                                                            to={`${RESERVATIONS_PATH}/${assign.target_id}`}
                                                                            target="_blank"
                                                                            className="font-semibold text-blue-650 hover:underline truncate"
                                                                        >
                                                                            {assign.title}
                                                                        </Link>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <IconReceipt size={13} className="text-indigo-600 shrink-0" />
                                                                        <span className="font-semibold text-slate-700 truncate">
                                                                            {assign.title}
                                                                        </span>
                                                                    </>
                                                                )}
                                                            </div>
                                                            <div className="col-span-3 text-right font-bold text-slate-800">
                                                                +${formatPrice(assign.amount)} <span className="text-xxs font-normal text-slate-400">{assign.currency}</span>
                                                            </div>
                                                            <div className="col-span-3 text-right text-slate-500 font-medium flex items-center justify-end gap-1">
                                                                <span>${formatPrice(assign.owed_before)}</span>
                                                                <IconArrowRight size={10} className="text-slate-400" />
                                                                <span className={assign.owed_after === 0 ? 'text-emerald-700 font-bold' : 'text-amber-700 font-bold'}>
                                                                    ${formatPrice(assign.owed_after)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* Footer Actions */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <span className="text-xs text-slate-500 font-medium">
                        {selectedCount > 0 && (
                            <span>{t('auto_assign_confirm_count', { count: selectedCount })}</span>
                        )}
                    </span>
                    <div className="flex items-center gap-2">
                        <Button
                            size="xs"
                            variant="subtle"
                            color="gray"
                            onClick={onClose}
                            disabled={confirmMutation.isPending}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            size="xs"
                            color="amber"
                            variant="filled"
                            leftSection={<IconWand size={14} />}
                            disabled={selectedCount === 0 || confirmMutation.isPending || previewMutation.isPending}
                            loading={confirmMutation.isPending}
                            onClick={handleConfirm}
                        >
                            {t('auto_assign_confirm')}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
